<template>
    <div class="OlpaSystemMemoView">
        <div class="top-section-wrapper">
            <div class="progress-bar-wrapper">
                <div class="progress-bar" :style="{ width: progressBarPercentageWidth }" />
            </div>
            <div class="exit-button-wrapper" @click="$router.go(-1)">Exit</div>
        </div>

        <carousel ref="carousel" :per-page="1" :pagination-enabled="false">
            <slide v-for="item in step.items[currentLocale]" :key="item.name" class="slide">
                <h1>{{ item.number }}</h1>
                <div class="step-content-wrapper">
                    <div class="first-part">
                        <div class="first-part-left-side">
                            <p>{{ $t('object') }}</p>
                            <div class="frame-wrapper">
                                <AnimatedNumbershape
                                    class="numbershape"
                                    color="black"
                                    :number="item.number"
                                    reversed
                                    :play="true"
                                />
                            </div>
                            <div class="direction-line-wrapper">
                                <div class="line" />
                            </div>
                        </div>
                        <div class="first-part-right-side">
                            <h4>{{ item.data.object_code }}</h4>
                            <p>{{ item.data.animation_description }}</p>
                            <p class="cursive-text">{{ $t('lifeskillSymbol') }}</p>
                            <h4 class="cursive-header"> {{ item.data.life_area }} </h4>
                            <p class="cursive-text">{{ $t('connect') }}</p>
                        </div>
                    </div>
                    <div class="second-part">
                        <div class="second-part-left-side">
                            <p>{{ $t('person') }}</p>
                            <div
                                class="image-wrapper"
                                :style="{ backgroundImage: `url(${createImagePath(item.number, 'person')})` }"
                            />
                        </div>
                        <div class="second-part-right-side">
                            <h4>{{ item.data.person_code }}</h4>
                            <p> {{ item.data.person_explanation }}</p>
                        </div>
                    </div>
                    <div class="third-part">
                        <div class="third-part-left-side">
                            <p>{{ $t('action') }}</p>
                            <div
                                class="image-wrapper"
                                :style="{ backgroundImage: `url(${createImagePath(item.number, 'action')})` }"
                            />
                        </div>
                        <div class="third-part-right-side">
                            <h4>{{ item.data.action_code }}</h4>
                            <p> {{ item.data.action_explanation }}</p>
                        </div>
                    </div>
                </div>
            </slide>
        </carousel>
        <div class="direction-buttons-wrapper" :class="{ isLastSlide }">
            <button :disabled="!currentPage" @click="goToPreviousSlide">
                <svgicon icon="arrow-left" class="arrow-icon" />
            </button>
            <button v-if="!isLastSlide" :disabled="isLastSlide" @click="goToNextSlide">
                <svgicon icon="arrow-right" class="arrow-icon" />
            </button>
            <button v-else class="start-quiz-button" @click="goToQuizView">Start quiz</button>
        </div>
    </div>
</template>

<translations>
    object: object
    object_no: objekt
    person: person
    person_no: person
    lifeskillSymbol: Symbolizes lifeskill
    lifeskillSymbol_no: Symboliserer livsområdet
    connect: Connects to person and action
    connect_no: Kobles til person og handling
    action: action
    action_no: handling
</translations>

<script>
import { mapGetters } from 'vuex';
import { Carousel, Slide } from 'vue-carousel';

import AnimatedNumbershape from '@/components/AnimatedNumbershape';
import memoMixin from './memo.mixin';

export default {
    components: {
        Carousel,
        Slide,
        AnimatedNumbershape
    },

    mixins: [memoMixin],

    methods: {
        createImagePath(imageName, imageType) {
            // we can't have line breaks in src string thats why disable pretttier for this line
            // prettier-ignore
            return require(`@/assets/images/system-images/${this.urlifyString(this.system.name)}/${imageType}/${imageName}.jpg`);
        }
    }
};
</script>

<style lang="scss" scoped>
.OlpaSystemMemoView {
    width: 375px;
    height: 809px;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 10px;
    .top-section-wrapper {
        .progress-bar-wrapper {
            .progress-bar {
                transition: 0.3s;
                height: 3px;
                background: white;
            }
        }
        .exit-button-wrapper {
            text-align: right;
            margin-top: 10px;
            color: hsla(0, 0%, 100%, 0.5);
        }
    }
    .slide {
        background-color: rgba(0, 0, 0, 0.1);
        max-height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
        border-radius: 10px;
        margin-top: 20px;
        h1 {
            font-weight: normal;
            font-size: 30px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 0;
            padding: 10px 0;
        }
        .step-content-wrapper {
            width: 100%;
            padding: 10px;
            .first-part {
                display: flex;
                .first-part-left-side {
                    padding: 10px;
                    p {
                        text-transform: uppercase;
                        color: hsla(0, 0%, 100%, 0.5);
                        font-size: 12px;
                        text-align: center;
                    }
                    .frame-wrapper {
                        width: 90px;
                        height: 90px;
                        background: white;
                        border-radius: 10px;
                        .numbershape {
                            width: 100%;
                            height: 100%;
                        }
                    }
                    .direction-line-wrapper {
                        display: flex;
                        justify-content: center;
                        height: 90px;
                        position: relative;
                        .line {
                            width: 1px;
                            height: 100%;
                            background: hsla(0, 0%, 100%, 0.5);
                            &:after {
                                content: '';
                                position: absolute;
                                width: 0;
                                height: 0;
                                border-left: 6px solid transparent;
                                border-right: 6px solid transparent;
                                border-top: 10px solid white;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%);
                            }
                        }
                    }
                }
                .first-part-right-side {
                    padding: 10px;
                    h4 {
                        font-size: 15px;
                        margin: 16px 0;
                    }
                    h4:nth-of-type(1) {
                        margin: 12px 0;
                    }
                    p {
                        font-size: 15px;
                        color: hsla(0, 0%, 100%, 0.7);
                    }
                    p:nth-of-type(2) {
                        margin-top: 50px;
                    }
                    p:nth-of-type(3) {
                        margin-top: 0;
                    }
                    .cursive-header {
                        font-style: italic;
                        font-size: 13.5px;
                    }
                    .cursive-text {
                        font-style: italic;
                        font-size: 13.5px;
                    }
                }
            }
            .second-part {
                display: flex;
                .second-part-left-side {
                    padding: 10px;
                    p {
                        text-transform: uppercase;
                        color: hsla(0, 0%, 100%, 0.5);
                        font-size: 12px;
                        text-align: center;
                        margin-top: 0;
                    }

                    .image-wrapper {
                        width: 90px;
                        height: 90px;
                        border-radius: 10px;
                        background-position: center;
                        background-size: cover;
                    }
                }
                .second-part-right-side {
                    padding: 10px;
                    h4 {
                        font-size: 15px;
                        margin: 0;
                    }
                    p {
                        font-size: 15px;
                        color: hsla(0, 0%, 100%, 0.7);
                        margin: 15px 0;
                    }
                }
            }
            .third-part {
                display: flex;
                .third-part-left-side {
                    padding: 10px;
                    p {
                        text-transform: uppercase;
                        color: hsla(0, 0%, 100%, 0.5);
                        font-size: 12px;
                        text-align: center;
                        margin-top: 0;
                    }

                    .image-wrapper {
                        width: 90px;
                        height: 90px;
                        border-radius: 10px;
                        background-position: center;
                        background-size: cover;
                    }
                }
                .third-part-right-side {
                    padding: 10px;
                    h4 {
                        font-size: 15px;
                        margin: 0;
                    }
                    p {
                        font-size: 15px;
                        color: hsla(0, 0%, 100%, 0.7);
                        margin: 15px 0 0;
                    }
                }
            }
        }
    }
    .direction-buttons-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        transition: 0.5s;
        width: 100%;
        position: relative;
        button {
            width: 35px;
            height: 35px;
            margin: 10px;
            background: inherit;
            border: none;
            outline: none;
            cursor: pointer;
            &:disabled {
                opacity: 0.5;
            }
            .arrow-icon {
                width: 100%;
                height: 100%;
                fill: white;
            }
        }

        .start-quiz-button {
            min-width: 100px;
            background-color: white;
            border-radius: 5px;
            font-size: 16px;
            line-height: 35px;
            margin: 0;
        }

        &.isLastSlide {
            width: calc(50% + 50px);
            height: 55px;

            button:first-of-type {
                position: absolute;
                right: 150px;
            }
            .start-quiz-button {
                position: absolute;
                right: 0;
            }
        }
    }
}
</style>
